import { useEffect, useMemo, useState } from "react";
import type { QueueBoardRoom } from "../types";
import { queueFont } from "../queueFont";
import { CareCornerLogo } from "./CareCornerLogo";
import { useSideVideoLayout } from "./SideVideoLayoutContext";
import { WelcomeHeader } from "./WelcomeHeader";

/** Max doctor/room cards shown at once when fix pagination is on. */
export const QUEUE_BOARD_MAX_PER_PAGE = 8;
const PAGE_ROTATE_MS = 10000;

type RoomCardsGridProps = {
  clinicName: string;
  rooms: QueueBoardRoom[];
  /**
   * When true, show at most 8 cards per page and auto-rotate if there are more.
   * Enable via `?fix=1` (or `?fixed=1`) on the queue URL.
   */
  fixPagination?: boolean;
};

function isTruthyFlag(raw: string | null | undefined): boolean {
  const v = String(raw ?? "")
    .trim()
    .toLowerCase();
  return v === "1" || v === "true" || v === "yes" || v === "on";
}

export function parseFixPaginationFlag(query: URLSearchParams): boolean {
  return isTruthyFlag(query.get("fix")) || isTruthyFlag(query.get("fixed"));
}

/** Column layout only — card size/type scale is pure CSS via [data-count]. */
function gridColsClass(count: number, fixPagination: boolean): string {
  if (fixPagination) return "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4";
  if (count <= 1) return "grid-cols-1";
  if (count === 2) return "grid-cols-2";
  if (count === 3) return "grid-cols-3";
  if (count === 4) return "grid-cols-2 lg:grid-cols-4";
  if (count <= 6) return "grid-cols-3";
  if (count <= 8) return "grid-cols-4";
  if (count <= 9) return "grid-cols-3";
  return "grid-cols-4 xl:grid-cols-5";
}

function RoomCard({ room }: { room: QueueBoardRoom }) {
  const roomNo = String(room.id ?? "").trim();
  const title = String(room.name ?? "").trim();
  const roomLabel = roomNo ? `Room ${roomNo}` : "";
  // Avoid "Room 1" twice when title is already the room label
  const titleLooksLikeRoom =
    !!roomNo &&
    (title.toLowerCase() === roomLabel.toLowerCase() ||
      title.toLowerCase() === roomNo.toLowerCase());
  const showTitle = !!title && !titleLooksLikeRoom;
  const showRoomNo = !!roomLabel;

  return (
    <div
      className={`${queueFont} nq-card flex h-full min-h-0 w-full min-w-0 flex-col items-center justify-center border border-solid border-[#cbd5e1] bg-gradient-to-b from-slate-50 to-white text-center shadow-[0_4px_16px_rgba(15,23,42,0.06)]`}
    >
      <div className="nq-card-inner flex max-w-full min-h-0 flex-col items-center">
        {showTitle ? (
          <div
            className={`${queueFont} nq-name w-full break-words font-extrabold leading-snug text-[#0b3a66] [overflow-wrap:anywhere]`}
          >
            {title}
          </div>
        ) : null}
        {showRoomNo ? (
          <div
            className={`${queueFont} nq-room w-full break-words font-extrabold leading-snug text-[#0b3a66] [overflow-wrap:anywhere]`}
          >
            {roomLabel}
          </div>
        ) : null}
        <div
          className={`${queueFont} nq-doctor w-full break-words font-semibold leading-snug text-[#334155] [overflow-wrap:anywhere]`}
        >
          {room.doctorName}
        </div>
        <div className="nq-label font-bold uppercase leading-tight tracking-[0.12em] text-slate-500">
          Now serving
        </div>
        <div
          className={`${queueFont} nq-queue w-full font-extrabold leading-none text-[#0b3a66] [font-variant-numeric:tabular-nums]`}
        >
          {room.currentQueue}
        </div>
      </div>
    </div>
  );
}

/**
 * Pure CSS size scale by visible card count.
 * Fewer cards → larger type / padding; more cards → denser.
 */
const QUEUE_BOARD_COUNT_CSS = `
.nq-board[data-count="1"] { --nq-max-w: min(560px, 72vw); --nq-max-h: min(78vh, 720px); }
.nq-board[data-count="2"] { --nq-max-w: min(1100px, 92vw); --nq-max-h: min(72vh, 640px); }
.nq-board[data-count="3"] { --nq-max-w: min(1400px, 94vw); --nq-max-h: min(68vh, 560px); }
.nq-board[data-count="4"] { --nq-max-w: min(1600px, 96vw); --nq-max-h: min(70vh, 580px); }
.nq-board[data-count="5"],
.nq-board[data-count="6"] { --nq-max-w: min(1700px, 96vw); --nq-max-h: 100%; }
.nq-board[data-count="7"],
.nq-board[data-count="8"],
.nq-board[data-count="9"],
.nq-board[data-count="10"],
.nq-board[data-count="11"],
.nq-board[data-count="12"] { --nq-max-w: min(1800px, 96vw); --nq-max-h: 100%; }

.nq-board { --nq-max-w: min(1800px, 96vw); --nq-max-h: 100%; }

.nq-board .nq-grid-shell {
  width: 100%;
  max-width: var(--nq-max-w);
  max-height: var(--nq-max-h);
  height: var(--nq-max-h);
}

.nq-board[data-count="1"] .nq-card { border-radius: 1rem; padding: clamp(16px, 2.5vmin, 32px) clamp(20px, 3vmin, 40px); }
.nq-board[data-count="1"] .nq-card-inner { gap: clamp(10px, 2vmin, 20px); }
.nq-board[data-count="1"] .nq-name { font-size: clamp(18px, 2.6vmin, 28px); }
.nq-board[data-count="1"] .nq-room { font-size: clamp(22px, 3.2vmin, 34px); }
.nq-board[data-count="1"] .nq-doctor { font-size: clamp(16px, 2.4vmin, 24px); }
.nq-board[data-count="1"] .nq-label { font-size: clamp(12px, 1.6vmin, 15px); }
.nq-board[data-count="1"] .nq-queue { font-size: clamp(96px, 18vmin, 220px); }

.nq-board[data-count="2"] .nq-card { border-radius: 1rem; padding: clamp(14px, 2.2vmin, 28px) clamp(16px, 2.5vmin, 36px); }
.nq-board[data-count="2"] .nq-card-inner { gap: clamp(8px, 1.6vmin, 16px); }
.nq-board[data-count="2"] .nq-name { font-size: clamp(16px, 2.2vmin, 24px); }
.nq-board[data-count="2"] .nq-room { font-size: clamp(20px, 2.8vmin, 30px); }
.nq-board[data-count="2"] .nq-doctor { font-size: clamp(15px, 2.1vmin, 22px); }
.nq-board[data-count="2"] .nq-label { font-size: clamp(11px, 1.5vmin, 14px); }
.nq-board[data-count="2"] .nq-queue { font-size: clamp(80px, 15vmin, 180px); }

.nq-board[data-count="3"] .nq-card { border-radius: 0.75rem; padding: clamp(12px, 2vmin, 24px) clamp(12px, 2vmin, 28px); }
.nq-board[data-count="3"] .nq-card-inner { gap: clamp(6px, 1.3vmin, 14px); }
.nq-board[data-count="3"] .nq-name { font-size: clamp(14px, 2vmin, 20px); }
.nq-board[data-count="3"] .nq-room { font-size: clamp(17px, 2.4vmin, 26px); }
.nq-board[data-count="3"] .nq-doctor { font-size: clamp(13px, 1.8vmin, 18px); }
.nq-board[data-count="3"] .nq-label { font-size: clamp(10px, 1.3vmin, 13px); }
.nq-board[data-count="3"] .nq-queue { font-size: clamp(64px, 12vmin, 140px); }

.nq-board[data-count="4"] .nq-card { border-radius: 0.75rem; padding: clamp(10px, 1.6vmin, 20px) clamp(10px, 1.8vmin, 24px); }
.nq-board[data-count="4"] .nq-card-inner { gap: clamp(5px, 1.1vmin, 12px); }
.nq-board[data-count="4"] .nq-name { font-size: clamp(13px, 1.8vmin, 18px); }
.nq-board[data-count="4"] .nq-room { font-size: clamp(15px, 2.1vmin, 22px); }
.nq-board[data-count="4"] .nq-doctor { font-size: clamp(12px, 1.6vmin, 16px); }
.nq-board[data-count="4"] .nq-label { font-size: clamp(10px, 1.2vmin, 12px); }
.nq-board[data-count="4"] .nq-queue { font-size: clamp(52px, 10vmin, 120px); }

.nq-board[data-count="5"] .nq-card,
.nq-board[data-count="6"] .nq-card { border-radius: 0.75rem; padding: clamp(8px, 1.2vmin, 16px) clamp(8px, 1.5vmin, 18px); }
.nq-board[data-count="5"] .nq-card-inner,
.nq-board[data-count="6"] .nq-card-inner { gap: clamp(4px, 1vmin, 10px); }
.nq-board[data-count="5"] .nq-name,
.nq-board[data-count="6"] .nq-name { font-size: clamp(12px, 1.6vmin, 15px); }
.nq-board[data-count="5"] .nq-room,
.nq-board[data-count="6"] .nq-room { font-size: clamp(14px, 1.9vmin, 18px); }
.nq-board[data-count="5"] .nq-doctor,
.nq-board[data-count="6"] .nq-doctor { font-size: clamp(11px, 1.4vmin, 15px); }
.nq-board[data-count="5"] .nq-label,
.nq-board[data-count="6"] .nq-label { font-size: 10px; }
.nq-board[data-count="5"] .nq-queue,
.nq-board[data-count="6"] .nq-queue { font-size: clamp(44px, 8.5vmin, 96px); }

.nq-board[data-count="7"] .nq-card,
.nq-board[data-count="8"] .nq-card { border-radius: 0.5rem; padding: 0.375rem 0.5rem; }
.nq-board[data-count="7"] .nq-card-inner,
.nq-board[data-count="8"] .nq-card-inner { gap: 0.25rem; }
.nq-board[data-count="7"] .nq-name,
.nq-board[data-count="8"] .nq-name { font-size: clamp(11px, 1.4vmin, 13px); }
.nq-board[data-count="7"] .nq-room,
.nq-board[data-count="8"] .nq-room { font-size: clamp(12px, 1.6vmin, 16px); }
.nq-board[data-count="7"] .nq-doctor,
.nq-board[data-count="8"] .nq-doctor { font-size: clamp(10px, 1.3vmin, 13px); }
.nq-board[data-count="7"] .nq-label,
.nq-board[data-count="8"] .nq-label { font-size: 9px; }
.nq-board[data-count="7"] .nq-queue,
.nq-board[data-count="8"] .nq-queue { font-size: clamp(36px, 7vmin, 72px); }

.nq-board:not([data-count="1"]):not([data-count="2"]):not([data-count="3"]):not([data-count="4"]):not([data-count="5"]):not([data-count="6"]):not([data-count="7"]):not([data-count="8"]) .nq-card {
  border-radius: 0.5rem; padding: 0.25rem 0.375rem;
}
.nq-board:not([data-count="1"]):not([data-count="2"]):not([data-count="3"]):not([data-count="4"]):not([data-count="5"]):not([data-count="6"]):not([data-count="7"]):not([data-count="8"]) .nq-card-inner { gap: 0.125rem; }
.nq-board:not([data-count="1"]):not([data-count="2"]):not([data-count="3"]):not([data-count="4"]):not([data-count="5"]):not([data-count="6"]):not([data-count="7"]):not([data-count="8"]) .nq-name { font-size: clamp(10px, 1.2vmin, 12px); }
.nq-board:not([data-count="1"]):not([data-count="2"]):not([data-count="3"]):not([data-count="4"]):not([data-count="5"]):not([data-count="6"]):not([data-count="7"]):not([data-count="8"]) .nq-room { font-size: clamp(11px, 1.4vmin, 14px); }
.nq-board:not([data-count="1"]):not([data-count="2"]):not([data-count="3"]):not([data-count="4"]):not([data-count="5"]):not([data-count="6"]):not([data-count="7"]):not([data-count="8"]) .nq-doctor { font-size: clamp(9px, 1.2vmin, 12px); }
.nq-board:not([data-count="1"]):not([data-count="2"]):not([data-count="3"]):not([data-count="4"]):not([data-count="5"]):not([data-count="6"]):not([data-count="7"]):not([data-count="8"]) .nq-label { font-size: 8px; }
.nq-board:not([data-count="1"]):not([data-count="2"]):not([data-count="3"]):not([data-count="4"]):not([data-count="5"]):not([data-count="6"]):not([data-count="7"]):not([data-count="8"]) .nq-queue { font-size: clamp(28px, 6vmin, 56px); }

/* Side-video stacked column */
.nq-board[data-stack="1"] .nq-card { border-radius: 0.75rem; padding: clamp(12px, 2vmin, 20px) clamp(12px, 2.5vmin, 24px); }
.nq-board[data-stack="1"][data-count="1"] .nq-queue,
.nq-board[data-stack="1"][data-count="2"] .nq-queue { font-size: clamp(72px, 16vmin, 140px); }
.nq-board[data-stack="1"][data-count="3"] .nq-queue,
.nq-board[data-stack="1"][data-count="4"] .nq-queue { font-size: clamp(52px, 11vmin, 96px); }
.nq-board[data-stack="1"]:not([data-count="1"]):not([data-count="2"]):not([data-count="3"]):not([data-count="4"]) .nq-queue {
  font-size: clamp(36px, 8vmin, 64px);
}
`;

export function RoomCardsGrid({ clinicName, rooms, fixPagination = false }: RoomCardsGridProps) {
  const sideVideo = useSideVideoLayout();
  const multiRoomColumnStack = sideVideo && rooms.length > 1;

  const pageSize = fixPagination ? QUEUE_BOARD_MAX_PER_PAGE : rooms.length || 1;
  const pageCount = Math.max(1, Math.ceil(rooms.length / pageSize));
  const [pageIndex, setPageIndex] = useState(0);

  useEffect(() => {
    setPageIndex((prev) => (prev >= pageCount ? 0 : prev));
  }, [pageCount, rooms.length]);

  useEffect(() => {
    if (!fixPagination || pageCount <= 1) return;
    const id = window.setInterval(() => {
      setPageIndex((prev) => (prev + 1) % pageCount);
    }, PAGE_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [fixPagination, pageCount]);

  const visibleRooms = useMemo(() => {
    if (!fixPagination || rooms.length <= QUEUE_BOARD_MAX_PER_PAGE) return rooms;
    const start = pageIndex * QUEUE_BOARD_MAX_PER_PAGE;
    return rooms.slice(start, start + QUEUE_BOARD_MAX_PER_PAGE);
  }, [fixPagination, rooms, pageIndex]);

  const scaleCount = visibleRooms.length;
  const denseHeader = scaleCount > 4;
  const gapClass =
    scaleCount <= 2
      ? "gap-[clamp(16px,2.5vmin,32px)]"
      : scaleCount <= 4
        ? "gap-[clamp(12px,1.8vmin,24px)]"
        : scaleCount <= 6
          ? "gap-[clamp(10px,1.5vmin,20px)]"
          : "gap-[clamp(8px,1.2vmin,16px)]";

  return (
    <div
      className={[
        queueFont,
        "nq-board relative box-border flex w-full max-w-[100vw] select-none flex-col overflow-hidden bg-white antialiased",
        multiRoomColumnStack
          ? "h-full min-h-0 flex-1"
          : "h-[100dvh] max-h-[100dvh] min-h-0",
      ].join(" ")}
      data-count={scaleCount}
      data-stack={multiRoomColumnStack ? "1" : "0"}
    >
      <style>{QUEUE_BOARD_COUNT_CSS}</style>

      {!multiRoomColumnStack ? <CareCornerLogo variant="grid" /> : null}

      <WelcomeHeader
        clinicName={clinicName}
        layout="inline"
        compact={multiRoomColumnStack || denseHeader}
      />

      <div
        className={[
          "flex min-h-0 w-full flex-1 flex-col overflow-hidden",
          multiRoomColumnStack
            ? "items-stretch px-3 pb-3 pt-1"
            : "items-center justify-center px-[clamp(16px,2.5vmin,48px)] pb-[clamp(56px,7vmin,96px)] pt-1",
        ].join(" ")}
      >
        {rooms.length === 0 ? (
          <div
            className={`${queueFont} max-w-[520px] self-center p-[clamp(24px,4vmin,48px)] text-center text-lg leading-normal text-[#334155]`}
          >
            No rooms are configured for this clinic yet. Add rooms in Settings (Manage time → Rooms), then
            refresh this page.
          </div>
        ) : multiRoomColumnStack ? (
          <div className="flex h-full min-h-0 w-full flex-1 flex-col gap-2">
            {visibleRooms.map((room) => (
              <RoomCard key={room.id} room={room} />
            ))}
            {fixPagination && pageCount > 1 ? (
              <div className="shrink-0 pt-1 text-center text-[10px] font-semibold tracking-wide text-slate-400">
                {pageIndex + 1} / {pageCount}
              </div>
            ) : null}
          </div>
        ) : (
          <div className="nq-grid-shell flex flex-col gap-2">
            <div
              className={[
                "grid h-full min-h-0 w-full flex-1 auto-rows-fr",
                gridColsClass(scaleCount, fixPagination),
                gapClass,
              ].join(" ")}
            >
              {visibleRooms.map((room) => (
                <RoomCard key={room.id} room={room} />
              ))}
            </div>
            {fixPagination && pageCount > 1 ? (
              <div className="shrink-0 text-center text-sm font-semibold tracking-wide text-slate-400">
                Page {pageIndex + 1} of {pageCount}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
